import cn from 'classnames'
import AlertPanel, {
  AlertPanelProps,
} from 'ext-ui/components/AlertPanel/AlertPanel'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import React, { ReactElement, ReactNode } from 'react'

import {
  cardDetailListStyle,
  settingsStatusCardStyle,
} from './SettingsStatusCard.style'

export interface SettingsStatusCardBanner {
  variant: AlertPanelProps['variant']
  /** A rendered svg element (ext-ui / legacy Svg icon). */
  icon: ReactElement
  title: string
  description?: ReactNode
}

export interface SettingsStatusCardProps {
  title: string
  /** Inline element before the title — e.g. a green checkmark for a verified state. */
  statusIcon?: ReactNode
  banner?: SettingsStatusCardBanner
  /** Footer bar content — a CTA button, or plain status text for a locked/pending
   * state with nothing to click. */
  footer?: ReactNode
  children?: ReactNode
  testId?: string
}

/**
 * The shared "content card" shell both settings cards render into: an ext-ui
 * `ContentCard` panel composing an optional top `AlertPanel` banner, a title row
 * (optional inline status icon), a body slot, and an optional footer bar. Rebuilt
 * from the Invoice Pay prototype's own `ContentCard` using ext-ui primitives
 * (`ContentCard` + `AlertPanel`) — see the org-settings Bank Account page.
 */
export function SettingsStatusCard({
  title,
  statusIcon,
  banner,
  footer,
  children,
  testId,
}: SettingsStatusCardProps): ReactElement {
  return (
    <ContentCard data-testid={testId}>
      <div className="SettingsStatusCard" css={settingsStatusCardStyle}>
        {banner && (
          <AlertPanel variant={banner.variant} icon={banner.icon}>
            <span className="SettingsStatusCard-bannerBody">
              <span className="SettingsStatusCard-bannerTitle">
                {banner.title}
              </span>
              {banner.description && (
                <span className="SettingsStatusCard-bannerDescription">
                  {banner.description}
                </span>
              )}
            </span>
          </AlertPanel>
        )}
        <div className="SettingsStatusCard-header">
          <div className="SettingsStatusCard-titleGroup">
            {statusIcon}
            <h2 className="SettingsStatusCard-title">{title}</h2>
          </div>
        </div>
        {children && <div className="SettingsStatusCard-body">{children}</div>}
        {footer && <div className="SettingsStatusCard-footer">{footer}</div>}
      </div>
    </ContentCard>
  )
}

export function CardDetailList({
  children,
}: {
  children: ReactNode
}): ReactElement {
  return (
    <div className="CardDetailList" css={cardDetailListStyle}>
      {children}
    </div>
  )
}

export function CardDetailRow({
  label,
  value,
  variant = 'text',
  capitalize = false,
}: {
  label: string
  value: ReactNode
  variant?: 'text' | 'pill'
  capitalize?: boolean
}): ReactElement {
  return (
    <div className="CardDetailRow">
      <span className="CardDetailRow-label">{label}</span>
      {variant === 'pill' ? (
        <span className="CardDetailRow-pill">{value}</span>
      ) : (
        <span className={cn('CardDetailRow-value', { capitalize })}>
          {value}
        </span>
      )}
    </div>
  )
}
